import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// mylinksStats — capteur de trafic de la page /mylinks.
//
// Les mesures sont écrites dans `visitesiteweb` par une fonction Backend
// (`mylinks-visit`) qui capte l'IP réelle et la résout en pays/ville.
// Le navigateur ne peut pas connaître l'IP du visiteur, d'où le passage
// par le serveur.
//
// Marqueurs utilisés dans `source` :
//   • vue  →  « mylinks:<canal> »
//   • clic →  « mylinks-click:<type>:<id>:<titre> »
// ─────────────────────────────────────────────────────────────

const SESSION_KEY = 'zifek_mylinks_session';
const CLICK_KEY_PREFIX = 'zifek_mylinks_click_';

export const MYLINKS_VIEW_PREFIX = 'mylinks:';
export const MYLINKS_CLICK_PREFIX = 'mylinks-click:';

export function myLinksSessionId(): string {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const generated = `ml-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(SESSION_KEY, generated);
    return generated;
  } catch {
    return `ml-${Date.now()}`;
  }
}

/** Canal d'arrivée déduit du référent. */
export function myLinksChannel(): string {
  const ref = typeof document !== 'undefined' ? document.referrer || '' : '';
  if (/facebook|fb\.me/i.test(ref)) return 'facebook';
  if (/instagram/i.test(ref)) return 'instagram';
  if (/tiktok/i.test(ref)) return 'tiktok';
  if (/wa\.me|whatsapp/i.test(ref)) return 'whatsapp';
  if (/google/i.test(ref)) return 'google';
  if (/twitter|x\.com/i.test(ref)) return 'x';
  if (/linkedin/i.test(ref)) return 'linkedin';
  return ref ? 'autre' : 'direct';
}

interface TenantLike {
  id: number;
  nomcommerce?: string | null;
  name?: string | null;
  user_name?: string | null;
}

interface VisitPayload {
  merchantId: number;
  boutiqueName: string;
  subdomain: string;
  kind: 'view' | 'click';
  channel: string;
  targetKind?: string;
  targetId?: number;
  targetTitle?: string;
  page?: string;
}

async function sendVisit(payload: VisitPayload): Promise<void> {
  try {
    await supabase.functions.invoke('mylinks-visit', {
      body: { ...payload, session: myLinksSessionId() },
    });
  } catch {
    /* best-effort : une visite non enregistrée ne gêne jamais le visiteur */
  }
}

/** Enregistre une vue de la page MyLinks (une fois par session). */
export async function trackMyLinksView(tenant: TenantLike): Promise<void> {
  if (!tenant?.id) return;
  try {
    if (window.sessionStorage.getItem(`zifek_mylinks_${tenant.id}`)) return;
    window.sessionStorage.setItem(`zifek_mylinks_${tenant.id}`, '1');
  } catch {
    /* stockage indisponible : on mesure quand même */
  }

  await sendVisit({
    merchantId: tenant.id,
    boutiqueName: tenant.nomcommerce || tenant.name || '',
    subdomain: tenant.user_name || '',
    kind: 'view',
    channel: myLinksChannel(),
    page: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}

interface ClickInput {
  tenant: TenantLike;
  kind: 'product' | 'service' | 'annonce' | 'featured' | 'button' | 'site' | 'event' | 'blog';
  id?: number | null;
  title?: string | null;
}

function cleanTitle(title: string | null | undefined): string {
  return String(title || '')
    .replace(/[:|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
}

/** Enregistre un clic sur une offre (une fois par session et par offre). */
export async function trackMyLinksClick(input: ClickInput): Promise<void> {
  const { tenant, kind } = input;
  if (!tenant?.id) return;
  const targetId = Number(input.id) || 0;
  const key = `${CLICK_KEY_PREFIX}${kind}_${targetId}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, '1');
  } catch {
    /* stockage indisponible */
  }

  await sendVisit({
    merchantId: tenant.id,
    boutiqueName: tenant.nomcommerce || tenant.name || '',
    subdomain: tenant.user_name || '',
    kind: 'click',
    channel: myLinksChannel(),
    targetKind: kind,
    targetId,
    targetTitle: cleanTitle(input.title),
    page: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}

export interface MyLinksSourceCount {
  label: string;
  count: number;
}

export interface MyLinksOfferClick {
  kind: string;
  id: number;
  title: string;
  count: number;
}

export interface MyLinksDayPoint {
  label: string;
  count: number;
}

export interface MyLinksGeoCount {
  label: string;
  count: number;
}

export interface MyLinksStats {
  totalViews: number;
  weekViews: number;
  todayViews: number;
  uniqueVisitors: number;
  totalClicks: number;
  conversion: number;
  sources: MyLinksSourceCount[];
  countries: MyLinksGeoCount[];
  cities: MyLinksGeoCount[];
  topOffers: MyLinksOfferClick[];
  daily: MyLinksDayPoint[];
}

const KIND_LABEL: Record<string, string> = {
  product: 'Produit',
  service: 'Service',
  annonce: 'Annonce',
  featured: 'Offre à la une',
  button: 'Bouton',
  site: 'Site de la boutique',
  event: 'Événement',
  blog: 'Article',
};

export function myLinksKindLabel(kind: string): string {
  return KIND_LABEL[kind] || kind;
}

interface VisitRow {
  source: string | null;
  visiteur: string | null;
  date: string | null;
  pays: string | null;
  ville: string | null;
  ip: string | null;
}

/** Charge les statistiques agrégées des N derniers jours. */
export async function loadMyLinksStats(merchantId: number, days = 30): Promise<MyLinksStats> {
  const since = new Date(Date.now() - days * 24 * 3600 * 1000).toISOString();

  const { data, error } = await supabase
    .from('visitesiteweb')
    .select('source, visiteur, date, pays, ville, ip')
    .eq('boutique_id', merchantId)
    .or('source.like.mylinks:%,source.like.mylinks-click:%')
    .gte('date', since)
    .order('date', { ascending: false })
    .limit(5000);

  if (error) throw error;

  const rows = (data || []) as VisitRow[];
  const todayKey = new Date().toISOString().slice(0, 10);
  const weekLimit = Date.now() - 7 * 24 * 3600 * 1000;

  const visitors = new Set<string>();
  const sources = new Map<string, number>();
  const countries = new Map<string, number>();
  const cities = new Map<string, number>();
  const offers = new Map<string, MyLinksOfferClick>();
  const daily = new Map<string, number>();

  let totalViews = 0;
  let weekViews = 0;
  let todayViews = 0;
  let totalClicks = 0;

  rows.forEach((row) => {
    const source = String(row.source || '');
    const stamp = row.date ? new Date(row.date).getTime() : 0;

    if (source.startsWith(MYLINKS_CLICK_PREFIX)) {
      totalClicks += 1;
      const rest = source.slice(MYLINKS_CLICK_PREFIX.length);
      const [kind = 'autre', rawId = '0', ...titleParts] = rest.split(':');
      const id = Number(rawId) || 0;
      const key = `${kind}:${id}`;
      const existing = offers.get(key);
      if (existing) existing.count += 1;
      else offers.set(key, { kind, id, title: titleParts.join(':').trim() || myLinksKindLabel(kind), count: 1 });
      return;
    }

    if (!source.startsWith(MYLINKS_VIEW_PREFIX)) return;
    totalViews += 1;
    if (stamp >= weekLimit) weekViews += 1;
    if (row.date && String(row.date).slice(0, 10) === todayKey) todayViews += 1;
    if (row.visiteur) visitors.add(String(row.visiteur));

    const channel = source.slice(MYLINKS_VIEW_PREFIX.length).split(':')[0] || 'direct';
    sources.set(channel, (sources.get(channel) || 0) + 1);
    if (row.pays) countries.set(row.pays, (countries.get(row.pays) || 0) + 1);
    if (row.ville) cities.set(row.ville, (cities.get(row.ville) || 0) + 1);

    if (row.date) {
      const day = String(row.date).slice(0, 10);
      daily.set(day, (daily.get(day) || 0) + 1);
    }
  });

  // Série journalière complète (les jours sans visite restent à zéro).
  const dailySeries: MyLinksDayPoint[] = [];
  for (let i = 13; i >= 0; i -= 1) {
    const d = new Date(Date.now() - i * 24 * 3600 * 1000);
    const key = d.toISOString().slice(0, 10);
    dailySeries.push({
      label: d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
      count: daily.get(key) || 0,
    });
  }

  const toSorted = (map: Map<string, number>): MyLinksGeoCount[] =>
    Array.from(map.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

  return {
    totalViews,
    weekViews,
    todayViews,
    uniqueVisitors: visitors.size,
    totalClicks,
    conversion: totalViews > 0 ? Math.round((totalClicks / totalViews) * 1000) / 10 : 0,
    sources: toSorted(sources),
    countries: toSorted(countries),
    cities: toSorted(cities),
    topOffers: Array.from(offers.values()).sort((a, b) => b.count - a.count).slice(0, 10),
    daily: dailySeries,
  };
}